import { AVATAR_ID, Vec3 } from '../types'
import { FRAME_SECONDS, TIMER_TICK_SECONDS } from './constants'
import { findStateByName, overlapsAabb, showHudText } from './sharedHelpers'
import {
  BehaviorContext,
  BehaviorHandler,
  HudStyle,
  MissionBehaviors,
} from './types'

/**
 * RJS09（サーキット: 3 しゅう レース）のミッション環境ビヘイビア。
 *
 * テンプレート rbxl（src/missions/templates/RJS09.rbxl）のギミックを移植する:
 * - ServerScriptService/GameScript + StarterGui/GUILocalScript:
 *   - BindableEvent「スタート」受信でレースタイマーを開始する。タイマーは
 *     0 から毎秒 +1 で表示。Lua の start() にはデバウンスが無く、再受信では
 *     ループがもう 1 本走り同じ timer 変数を共有して進める（二重カウント）。
 *     ゴール後に再受信した場合もタイマーは 0 に戻らず続きから進む。
 *   - BindableEvent「ゴール」受信でタイマーを止め、GoalTextLabel
 *     「ゴール！！！」を表示する（以後出しっぱなし）。
 *   - 初期表示: CountTextLabel は Text=0 でプレイ開始時から見えている。
 * - ④ブロック/BlockScript（キックギミック）: キャラクター（または Car）が
 *   触れると Anchored を外し、(ブロック位置 - キャラ位置).unit * 50 の初速で
 *   弾き飛ばす。0.3 秒後に速度を 0.2 倍へ減衰し、デバウンス（hasKicked）を
 *   解除する。
 *
 * プレビューでの近似・意図的な差異:
 * - 「スタート」「ゴール」は学習者プログラムのメッセージ送信（実機の
 *   BindableEvent:Fire）で届くため onMessage に紐づける。
 * - スタートの多重受信は、実機どおりループを増やすと Blockly の forever
 *   送信で Promise ループと HUD 更新が無制限に増殖するため、常駐ループは
 *   1 本に畳み、加算量 activeStarts（生きている仮想ループ本数）で
 *   「N 本のループが毎秒 +1 ずつ → 毎秒 +N」の実機挙動を保存する。
 * - GUI のタイマー（CountTextLabel）とゴール表示（GoalTextLabel）は実機では
 *   別々の ScreenGui で同時に出るため、HUD を 'count' / 'goal' の 2 スロットに
 *   分けて再現する。ゴール後も左上のカウント（最終タイム）が残り、ゴールバナーと
 *   同時表示される。再スタートはカウントスロットを上書きして続きから進める。
 * - キックは物理エンジンの動的剛体ではなくキネマティック積分で近似する
 *   （World に既存オブジェクトへ初速を与える API が無いため）。重力
 *   （196.2 studs/s²）・Baseplate 上面（y=0）への着地・Wall* パーツとの
 *   AABB 衝突（軸分離）・接地中の摩擦減速（μ≈0.3 相当）を反映する。
 *   Truss 等その他パーツとの衝突は省略する。
 * - キック方向の .unit は実機ではゼロベクトルで NaN になるが、プレビューは
 *   中心一致時に何もしない（安全側）。
 * - ④ブロック/BlockScript の Anchored 連動（Anchored 化の瞬間に速度を恒久的に
 *   ゼロ化）は、objectBodyRegistry がこの Script パターンを検出し、再アンカー時に
 *   速度を退避しない（次のアンカー解除で必ず静止状態から再開する）ため移植不要。
 *
 * Car（運転できる車両）対応:
 * - シーン JSON の vehicles と物理側 VehicleSystem が運転を担う。乗車中に車体が
 *   ④ブロックへ触れると onVehicleTouch → emitTouch 経由で onTouchCharacter が発火し、
 *   下記 onBlockTouched が走る（実機 BlockScript の Car 衝突分岐の再現）。乗車中は
 *   アバターの World 位置が座席へ同期される（applySeatedAvatarPose）ため、
 *   (ブロック位置 - アバター位置) のキック方向は車体基準と一致し、歩行時と同じ
 *   ロジックで正しく前方へ蹴れる。
 *
 * 未対応:
 * - ③スタートちてん の周回カウンタ Script: Blockly コンパイル産物（onMessage/
 *   onPlay 形式の学習者プログラム）であり環境ギミックではないため移植しない
 *   （プレビューでは学習者自身の Blockly プログラムが同じ役割を担う）。
 * - ①チェックポイント1 / ②チェックポイント2 の空 Script: 学習者枠のため対象外。
 * - BGM はシーン JSON の bgm 機構の担当（ビヘイビア対象外）。実機の 'Race Zone'
 *   はプロダクト指定の Sunrise（bgm-overrides.json）へ差し替え済み。
 */

/** 学習者プログラムが送るメッセージ名（ServerStorage の BindableEvent 名）。 */
const START_MESSAGE = 'スタート'
const GOAL_MESSAGE = 'ゴール'

/** キックギミックの対象ブロックのオブジェクト名。 */
const BLOCK_NAME = '④ブロック'

/** キック衝突の対象となる壁パーツの名前接頭辞（Wall / Wall1..Wall4）。 */
const WALL_NAME_PREFIX = 'Wall'

/** キックの初速（Lua: kickForce = 50）。 */
const KICK_SPEED = 50

/** キックの全力時間（Lua: wait(0.3)）。 */
const KICK_PHASE_SECONDS = 0.3

/** 全力時間の後の減衰率（Lua: AssemblyLinearVelocity *= 0.2）。 */
const KICK_DAMPING = 0.2

/** 重力加速度（Roblox の Workspace.Gravity 既定値、studs/s²）。 */
const GRAVITY = 196.2

/** 地面の上面 Y（Baseplate: pos.y=-8, size.y=16 → 上面 0）。 */
const GROUND_TOP_Y = 0

/** 接地中の摩擦減速（μ≈0.3 × 重力。Plastic 同士のすべりの近似）。 */
const FRICTION_DECELERATION = 0.3 * GRAVITY

/** これ未満の水平速度は静止とみなしてキックの積分を終える（studs/s）。 */
const REST_SPEED = 0.5

/** ゴール表示の文言（GoalTextLabel.Text の実測値）。 */
const GOAL_TEXT = 'ゴール！！！'

/**
 * カウント（タイマー）とゴール表示の HUD スロット。実機は CountTextLabel と
 * GoalTextLabel が別々の ScreenGui で同時に出るため、スロットを分けてゴール後も
 * カウント（最終タイム）を左上に残す（旧来は 1 スロットでゴール表示が上書きし、
 * カウントが消えていた）。
 */
const COUNT_SLOT = 'count'
const GOAL_SLOT = 'goal'

/** タイマー GUI の実機デザイン（CountTextLabel の実測値。左上の白バッジ）。 */
const TIMER_HUD_STYLE: HudStyle = {
  background: '#FFFFFF',
  color: '#000000',
  border: '#000000',
  borderWidth: 3,
  left: 0,
}

/** ゴール GUI の実機デザイン（GoalTextLabel の実測値。ピンクの帯）。 */
const GOAL_HUD_STYLE: HudStyle = {
  background: '#FFE5FF',
  color: '#00007F',
  border: '#FF007F',
  borderWidth: 5,
  wide: true,
  top: 60,
}

/** ベクトルの長さ。 */
const lengthOf = (v: Vec3): number =>
  Math.sqrt(v.x * v.x + v.y * v.y + v.z * v.z)

/** ベクトルのスカラー倍（新しいオブジェクトを返す）。 */
const scaleVec = (v: Vec3, factor: number): Vec3 => ({
  x: v.x * factor,
  y: v.y * factor,
  z: v.z * factor,
})

/**
 * 指定の位置・寸法の箱が壁（Wall* かつ CanCollide）のどれかと重なるか。
 * キックの軸分離衝突判定に使う。
 */
const hitsWall = (
  ctx: BehaviorContext,
  position: Vec3,
  size: Vec3,
): boolean => {
  for (const state of ctx.world.getState().values()) {
    if (!state.name.startsWith(WALL_NAME_PREFIX) || !state.canCollide) {
      continue
    }
    if (overlapsAabb(position, size, state.position, state.size)) {
      return true
    }
  }
  return false
}

export const createRjs09Behaviors = (): MissionBehaviors => {
  // Lua: GUILocalScript の timer / isRace（timer はゴール後も保持される）。
  let timer = 0
  let isRace = false
  // 生きている仮想タイマーループの本数（Lua: start() ごとに増えるループ）。
  let activeStarts = 0
  // 常駐ループ（実体は 1 本だけ）の起動済みフラグ。
  let isTimerLoopRunning = false
  // Lua: BlockScript の hasKicked デバウンス。
  let hasKicked = false
  // キック積分ループの世代トークン（再キックで旧ループを失効させる。
  // Lua の AssemblyLinearVelocity 再代入が旧速度を上書きするのと同じ効果）。
  let kickToken = 0

  /** 初期表示（CountTextLabel は Text=0 でプレイ開始時から見えている）。 */
  const showInitialTimer: BehaviorHandler = showHudText(
    '0',
    TIMER_HUD_STYLE,
    COUNT_SLOT,
  )

  /**
   * スタート受信（Lua: GameScript の start → GUILocalScript の start）。
   * Lua にデバウンスは無く、多重受信ではループが複数本走って同じ timer を
   * 共有して進める。プレビューはループ増殖（forever 送信で Promise が
   * 無制限に増える）を避けるため常駐ループを 1 本に畳み、Lua の
   * 「N 本が毎秒 +1 ずつ」を「1 本が毎秒 +activeStarts」で保存する。
   */
  const onStart: BehaviorHandler = async (ctx) => {
    activeStarts += 1
    isRace = true
    if (isTimerLoopRunning) {
      return
    }
    isTimerLoopRunning = true
    while (isRace && !ctx.signal.aborted) {
      ctx.setHudText?.(String(timer), TIMER_HUD_STYLE, COUNT_SLOT)
      await ctx.wait(TIMER_TICK_SECONDS)
      // Lua: 各ループの timer += 1。ゴール直後の最終加算（待機明けに +1 して
      // から isRace を見て exit する）も含めて N 本ぶんまとめて加算する。
      timer += activeStarts
    }
    // 全仮想ループ終了（Lua: isRace=false で全ループが順次 exit）。
    // 次のスタートは新しい 1 本から数え直す。
    isTimerLoopRunning = false
    activeStarts = 0
  }

  /**
   * ゴール受信（Lua: GameScript の finish → GUILocalScript の finish）。
   * カウントとは別スロットに出すため、ゴール後も左上のカウント（最終タイム）が
   * 残る（実機の CountTextLabel と GoalTextLabel の同時表示に揃える）。
   */
  const onGoal: BehaviorHandler = (ctx) => {
    isRace = false
    ctx.setHudText?.(GOAL_TEXT, GOAL_HUD_STYLE, GOAL_SLOT)
  }

  /**
   * キックの積分ループ。毎フレーム、重力 → 軸分離の壁衝突つき移動 →
   * 地面クランプ → 接地摩擦 の順で進め、0.3 秒経過時に一度だけ減衰と
   * デバウンス解除を行う（Lua: wait(0.3) 後の *= 0.2 と hasKicked = false）。
   */
  const runKickMotion = async (
    ctx: BehaviorContext,
    blockId: string,
    startVelocity: Vec3,
    token: number,
  ): Promise<void> => {
    let velocity = startVelocity
    let elapsedSeconds = 0
    let isDamped = false
    while (kickToken === token && !ctx.signal.aborted) {
      const state = ctx.world.getState().get(blockId)
      if (!state) {
        // 学習者プログラムが destroy した（Lua も Part 消滅でスクリプトが止まる）。
        return
      }
      // 重力（semi-implicit Euler: 速度を先に更新してから位置を進める）。
      velocity = { ...velocity, y: velocity.y - GRAVITY * FRAME_SECONDS }
      // X/Z は軸分離で壁と衝突判定し、ぶつかった軸の速度を殺す。
      const moved = { ...state.position }
      const candidateX = moved.x + velocity.x * FRAME_SECONDS
      if (hitsWall(ctx, { ...moved, x: candidateX }, state.size)) {
        velocity = { ...velocity, x: 0 }
      } else {
        moved.x = candidateX
      }
      const candidateZ = moved.z + velocity.z * FRAME_SECONDS
      if (hitsWall(ctx, { ...moved, z: candidateZ }, state.size)) {
        velocity = { ...velocity, z: 0 }
      } else {
        moved.z = candidateZ
      }
      // Y は Baseplate 上面でクランプする（壁は水平移動で止まるため対象外）。
      const halfHeight = state.size.y / 2
      const candidateY = moved.y + velocity.y * FRAME_SECONDS
      const isGrounded = candidateY - halfHeight <= GROUND_TOP_Y
      if (isGrounded) {
        moved.y = GROUND_TOP_Y + halfHeight
        velocity = { ...velocity, y: 0 }
      } else {
        moved.y = candidateY
      }
      ctx.world.setPosition(blockId, moved)
      elapsedSeconds += FRAME_SECONDS
      if (!isDamped && elapsedSeconds >= KICK_PHASE_SECONDS - 1e-9) {
        isDamped = true
        velocity = scaleVec(velocity, KICK_DAMPING)
        hasKicked = false
      }
      if (isGrounded) {
        // 接地中は摩擦で水平速度を減速する。
        const horizontalSpeed = Math.hypot(velocity.x, velocity.z)
        if (horizontalSpeed > 0) {
          const slowed = Math.max(
            0,
            horizontalSpeed - FRICTION_DECELERATION * FRAME_SECONDS,
          )
          const factor = slowed / horizontalSpeed
          velocity = {
            x: velocity.x * factor,
            y: velocity.y,
            z: velocity.z * factor,
          }
        }
        if (isDamped && Math.hypot(velocity.x, velocity.z) < REST_SPEED) {
          return
        }
      }
      await ctx.wait(FRAME_SECONDS)
    }
  }

  /**
   * ④ブロック接触（Lua: BlockScript の onTouch）。デバウンス中でなければ
   * (ブロック位置 - アバター位置).unit * 50 の初速で弾き飛ばす。
   */
  const onBlockTouched: BehaviorHandler = (ctx) => {
    if (hasKicked) {
      return
    }
    const block = findStateByName(ctx.world, BLOCK_NAME)
    const avatar = ctx.world.getState().get(AVATAR_ID)
    if (!block || !avatar) {
      return
    }
    const direction = {
      x: block.position.x - avatar.position.x,
      y: block.position.y - avatar.position.y,
      z: block.position.z - avatar.position.z,
    }
    const distance = lengthOf(direction)
    if (distance < 1e-6) {
      // 中心が一致して向きが定まらない（実機では NaN になるケース）。
      return
    }
    hasKicked = true
    kickToken += 1
    return runKickMotion(
      ctx,
      block.id,
      scaleVec(direction, KICK_SPEED / distance),
      kickToken,
    )
  }

  const onMessage = new Map<string, BehaviorHandler>()
  onMessage.set(START_MESSAGE, onStart)
  onMessage.set(GOAL_MESSAGE, onGoal)

  const onTouchCharacter = new Map<string, BehaviorHandler>()
  onTouchCharacter.set(BLOCK_NAME, onBlockTouched)

  return { onPlay: [showInitialTimer], onMessage, onTouchCharacter }
}
